// src/pages/PaperTrading.js
import { useEffect, useRef, useState } from "react";
import api from "../utils/api";

import BrokerConnectAlpaca from "../components/BrokerConnectAlpaca";
import BrokerConnectTradier from "../components/BrokerConnectTradier";
import BalancesCard from "../components/BalancesCard";
import PlaceOrderCard from "../components/PlaceOrderCard";
import PlaceOptionOrderCard from "../components/PlaceOptionOrderCard1";
import PositionsAndOrdersCard from "../components/PositionsAndOrdersCard";
import TradeSettingsCard from "../components/TradeSettingsCard";
import AllTradesCard from "../components/AllTradesCard";

import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";

function getUserId() {
    return (
        localStorage.getItem("trader_id") ||
        localStorage.getItem("email") ||
        localStorage.getItem("user_id") ||
        ""
    );
}

/* ------------------------------------------------------------------ */
/*  Mode badge                                                         */
/* ------------------------------------------------------------------ */

function TradeModeBadge() {
    const [mode, setMode] = useState("paper");

    useEffect(() => {
        const raw = (localStorage.getItem("trade_mode") || "paper").toLowerCase();
        setMode(raw === "live" ? "live" : "paper");
    }, []);

    const label = mode === "live" ? "LIVE" : "PAPER";

    return (
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-800 border border-zinc-600 text-xs">
            <span
                className={`h-2 w-2 rounded-full ${mode === "live" ? "bg-red-400" : "bg-emerald-400"
                    } animate-pulse`}
            />
            <span className="font-semibold text-zinc-100">
                Trading Mode: {label}
            </span>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Broker header                                                      */
/* ------------------------------------------------------------------ */

function BrokerHeader() {
    return (
        <div className="p-4 bg-zinc-900 rounded-2xl flex items-center justify-between gap-4">
            <div>
                <h2 className="text-lg font-semibold text-zinc-50">
                    Connected Brokers
                </h2>
                <p className="text-xs text-zinc-400">
                    Link your brokerage APIs once, then trade from any page.
                </p>
            </div>
            <div className="flex gap-3">
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-900/60 border border-emerald-500/60 shadow-[0_0_16px_rgba(16,185,129,0.8)]">
                    <span className="h-2 w-2 rounded-full bg-emerald-300 animate-pulse" />
                    <span className="text-xs font-semibold text-emerald-200 tracking-[0.12em]">
                        ALPACA
                    </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-900/60 border border-indigo-400/60 shadow-[0_0_16px_rgba(129,140,248,0.8)]">
                    <span className="h-2 w-2 rounded-full bg-indigo-300 animate-pulse" />
                    <span className="text-xs font-semibold text-indigo-100 tracking-[0.12em]">
                        TRADIER
                    </span>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  AI: Place from Picks                                              */
/* ------------------------------------------------------------------ */

function PicksButtons({ userId, tradeMode }) {
    const [msg, setMsg] = useState("");

    const fire = async (url, body) => {
        if (!userId) {
            alert("Please log in first.");
            return;
        }
        const mode =
            (tradeMode || localStorage.getItem("trade_mode") || "paper").toLowerCase();

        setMsg("Working…");
        try {
            const { data } = await api.post(
                url,
                {
                    ...body,
                    mode,
                },
                { timeout: 60000 }
            );
            const text =
                data?.message ||
                data?.status ||
                (data?.ok ? "OK" : JSON.stringify(data || {}));
            setMsg(text);
        } catch (err) {
            console.error("PicksButtons error", err);
            const apiErr = err?.response?.data;
            const text =
                apiErr?.detail ||
                apiErr?.error ||
                apiErr?.message ||
                err?.message ||
                "Request failed.";
            setMsg(text);
        }
    };

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <h3 className="text-lg font-semibold text-zinc-100">
                Quick AI trades from Daily Picks
            </h3>
            <p className="text-xs text-zinc-400">
                Uses your current mode (&quot;paper&quot; or &quot;live&quot;) plus
                your per-trade size and risk settings.
            </p>
            <div className="flex flex-wrap gap-2">
                <button
                    onClick={() =>
                        fire("/api/ai/trade", {
                            user_id: userId,
                            strategy: "daily_picks",
                            max_trades: 1,
                        })
                    }
                    className="bg-blue-600 hover:bg-blue-500 rounded px-4 py-2 text-xs sm:text-sm font-semibold"
                >
                    Place Equity from Picks
                </button>
                <button
                    onClick={() =>
                        fire("/api/ai/options/trade", {
                            user_id: userId,
                            strategy: "daily_picks_options",
                            max_trades: 1,
                        })
                    }
                    className="bg-purple-600 hover:bg-purple-500 rounded px-4 py-2 text-xs sm:text-sm font-semibold"
                >
                    Place Options from Picks
                </button>
            </div>

            {msg && (
                <div className="text-xs text-zinc-200 whitespace-pre-wrap break-words">
                    {msg}
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Auto-trade readiness + summary                                   */
/* ------------------------------------------------------------------ */

function AutoTradeReadiness({ userId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        if (!userId) return;
        setLoading(true);
        setErr("");
        try {
            const { data } = await api.get("/api/ai/auto-trade-readiness", {
                params: { user_id: userId },
                timeout: 20000,
            });
            setData(data || {});
        } catch (e) {
            setErr("Failed to load readiness.");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId]);

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Auto-Trade Readiness</h3>
                <button
                    onClick={load}
                    className="bg-zinc-700 hover:bg-zinc-600 rounded px-3 py-1 text-xs"
                >
                    {loading ? "Checking…" : "Check"}
                </button>
            </div>
            {err && <div className="text-xs text-red-400">{err}</div>}
            {data && !err && (
                <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap max-h-40 overflow-y-auto border border-zinc-800 rounded p-2 bg-black/30">
                    {JSON.stringify(data, null, 2)}
                </pre>
            )}
        </div>
    );
}

function AutoTradeSummary({ userId }) {
    const [summary, setSummary] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        if (!userId) return;
        setLoading(true);
        setErr("");
        try {
            const { data } = await api.get("/api/ai/auto-trade-summary", {
                params: { user_id: userId },
                timeout: 20000,
            });

            if (!data || data.ok === false) {
                setErr(data?.error || "Could not load auto-trade history.");
                setSummary(null);
                setRecent([]);
            } else {
                setSummary({
                    total_runs: data.total_runs ?? 0,
                    successes: data.successes ?? 0,
                    errors: data.errors ?? 0,
                    last_run_at: data.last_run_at ?? data.last_run ?? null,
                });
                setRecent(data.recent || []);
            }
        } catch (e) {
            setErr("Could not load auto-trade history.");
            setSummary(null);
            setRecent([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId]);

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Auto-trade runs</h3>
                <button
                    onClick={load}
                    className="bg-zinc-700 hover:bg-zinc-600 rounded px-3 py-1 text-xs"
                >
                    {loading ? "Refreshing…" : "Refresh"}
                </button>
            </div>

            {err && <div className="text-xs text-red-400">{err}</div>}

            {summary && (
                <>
                    <div className="flex flex-wrap gap-4 text-xs">
                        <div>
                            <div className="text-zinc-400">Total</div>
                            <div className="text-zinc-50 font-semibold">
                                {summary.total_runs}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400">Last run</div>
                            <div className="text-zinc-50 font-semibold">
                                {summary.last_run_at || "—"}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400">Successes</div>
                            <div className="text-emerald-400 font-semibold">
                                {summary.successes}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400">Errors</div>
                            <div className="text-red-400 font-semibold">
                                {summary.errors}
                            </div>
                        </div>
                    </div>

                    <div className="text-xs text-zinc-400 mt-2 mb-1">
                        Last 10 auto-trades
                    </div>
                    <div className="max-h-40 overflow-y-auto text-[11px] space-y-1">
                        {recent.length === 0 && (
                            <div className="text-zinc-500">
                                No auto-trades logged yet.
                            </div>
                        )}
                        {recent.map((row) => (
                            <div
                                key={row.id}
                                className="flex justify-between py-1 border-b border-zinc-800 last:border-0"
                            >
                                <div className="mr-2 min-w-0">
                                    <div className="font-medium text-zinc-100 truncate">
                                        {row.symbol || row.option_symbol || "—"}
                                    </div>
                                    <div className="text-zinc-400">
                                        {row.asset_type} · {row.side} · {row.broker}
                                    </div>
                                    {row.strategy && (
                                        <div className="text-[10px] text-zinc-500 truncate">
                                            {row.strategy}
                                        </div>
                                    )}
                                </div>
                                <div className="text-right text-[11px]">
                                    <div
                                        className={
                                            (row.status || "").toLowerCase() ===
                                                "success"
                                                ? "text-emerald-400"
                                                : "text-amber-400"
                                        }
                                    >
                                        {row.status || "pending"}
                                    </div>
                                    <div className="text-zinc-500 truncate">
                                        {row.created_at}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}


/* ------------------------------------------------------------------ */
/*  Auto Exit Toggle Switch                                   */
/* ------------------------------------------------------------------ */

function AutoExitToggle({ userId }) {
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        if (!userId) return;
        setLoading(true);
        setErr("");
        try {
            const { data } = await api.get("/api/trade/settings", {
                params: { user_id: userId },
                timeout: 15000,
            });
            const flag = !!(data?.auto_exit_enabled);
            setEnabled(flag);
        } catch (e) {
            console.error("AutoExitToggle load error", e);
            setErr("Could not load auto-exit status.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId]);

    const toggle = async () => {
        if (!userId) {
            alert("Please log in first.");
            return;
        }
        setLoading(true);
        setErr("");
        try {
            const next = !enabled;
            await api.post(
                "/api/trade/settings",
                {
                    user_id: userId,
                    auto_exit_enabled: next,
                },
                { timeout: 15000 }
            );
            setEnabled(next);
        } catch (e) {
            console.error("AutoExitToggle save error", e);
            const apiErr = e?.response?.data;
            setErr(
                apiErr?.error ||
                apiErr?.message ||
                "Failed to update auto-exit setting."
            );
        } finally {
            setLoading(false);
        }
    };

    const statusLabel = enabled ? "ACTIVE" : "INACTIVE";
    const statusColor = enabled ? "text-emerald-400" : "text-red-400";
    const dotColor = enabled ? "bg-emerald-400" : "bg-red-400";

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-zinc-100">
                        Auto Exits
                    </h3>
                    <p className="text-xs text-zinc-400">
                        Turn the Smart Exit engine on/off for your open positions
                        when the cron runs.
                    </p>
                </div>
                <button
                    onClick={toggle}
                    disabled={loading || !userId}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-xs disabled:opacity-60"
                >
                    <span
                        className={`h-2.5 w-2.5 rounded-full ${dotColor} animate-pulse`}
                    />
                    <span className="font-semibold text-zinc-100">
                        {enabled ? "ON" : "OFF"}
                    </span>
                </button>
            </div>

            <div className="text-xs text-zinc-400">
                Status:{" "}
                <span className={`font-semibold ${statusColor}`}>
                    {statusLabel}
                </span>
            </div>

            {err && <div className="text-xs text-red-400">{err}</div>}

            <div className="text-[10px] text-zinc-500">
                Note: Auto Exits only run if you have open positions and your
                cron calls <code>/api/tasks/auto-manage-exits</code>.
            </div>
        </div>
    );
}



/* ------------------------------------------------------------------ */
/*  Trade analytics helpers                                           */
/* ------------------------------------------------------------------ */

function formatCurrency(n) {
    if (n === null || n === undefined) return "—";
    const value = Number(n) || 0;
    const sign = value < 0 ? "-" : "";
    return `${sign}$${Math.abs(value).toFixed(2)}`;
}

function PnlOverTimeChart({ data, viewMode }) {
    if (!data || data.length === 0) {
        return (
            <div className="text-xs text-zinc-500">
                No P/L history yet.
            </div>
        );
    }

    const isDaily = viewMode === "daily";

    return (
        <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: "#a1a1aa" }}
                    />
                    <YAxis
                        tick={{ fontSize: 10, fill: "#a1a1aa" }}
                        tickFormatter={(v) => `$${v.toFixed(0)}`}
                    />
                    <Tooltip
                        contentStyle={{
                            background: "#020617",
                            border: "1px solid #27272a",
                            fontSize: 11,
                        }}
                        formatter={(v, name) => [`$${v.toFixed(2)}`, name]}
                    />
                    <Legend
                        wrapperStyle={{
                            fontSize: 10,
                            color: "#a1a1aa",
                        }}
                    />

                    <Line
                        type="monotone"
                        dataKey={isDaily ? "pnlTotal" : "cumTotal"}
                        name={isDaily ? "Daily P/L (total)" : "Cumulative P/L"}
                        stroke="#22c55e"
                        strokeWidth={2}
                        dot={false}
                    />
                    <Line
                        type="monotone"
                        dataKey={isDaily ? "pnlEquity" : "cumEquity"}
                        name={
                            isDaily ? "Equity P/L (daily)" : "Equity P/L (cumulative)"
                        }
                        stroke="#60a5fa"
                        strokeWidth={1.5}
                        dot={false}
                    />
                    <Line
                        type="monotone"
                        dataKey={isDaily ? "pnlOption" : "cumOption"}
                        name={
                            isDaily
                                ? "Options P/L (daily)"
                                : "Options P/L (cumulative)"
                        }
                        stroke="#a855f7"
                        strokeWidth={1.5}
                        strokeDasharray="4 4"
                        dot={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function TickerHeatmap({ data, onSelectTicker }) {
    if (!data || data.length === 0) {
        return (
            <div className="text-xs text-zinc-500">
                No per-ticker P/L yet.
            </div>
        );
    }

    const sorted = [...data]
        .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
        .slice(0, 12);

    return (
        <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sorted}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis
                        dataKey="ticker"
                        tick={{ fontSize: 10, fill: "#a1a1aa" }}
                        interval={0}
                        angle={-30}
                        textAnchor="end"
                        height={60}
                    />
                    <YAxis
                        tick={{ fontSize: 10, fill: "#a1a1aa" }}
                        tickFormatter={(v) => `$${v.toFixed(0)}`}
                    />
                    <Tooltip
                        contentStyle={{
                            background: "#020617",
                            border: "1px solid #27272a",
                            fontSize: 11,
                        }}
                        formatter={(v, _, props) => [
                            `$${v.toFixed(2)}`,
                            `P/L (${props.payload.trades} trades)`,
                        ]}
                    />
                    <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                        {sorted.map((entry, index) => (
                            <Cell
                                key={`cell-${entry.ticker}-${index}`}
                                fill={entry.pnl >= 0 ? "#22c55e" : "#ef4444"}
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                    onSelectTicker && onSelectTicker(entry.ticker)
                                }
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function getTradeTicker(t) {
    return (
        (t.symbol || t.ticker || t.underlying_symbol || "UNKNOWN").toUpperCase()
    );
}

function EquityOptionsPie({ equityCount, optionCount }) {
    const total = (equityCount || 0) + (optionCount || 0);

    if (!total) {
        return (
            <div className="text-xs text-zinc-500">
                No trades yet to show allocation.
            </div>
        );
    }

    const data = [
        { name: "Equity", value: equityCount || 0 },
        { name: "Options", value: optionCount || 0 },
    ];

    const COLORS = ["#60a5fa", "#a855f7"];

    return (
        <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={60}
                        innerRadius={30}
                        paddingAngle={3}
                    >
                        {data.map((entry, index) => (
                            <Cell
                                key={`cell-${entry.name}-${index}`}
                                fill={COLORS[index % COLORS.length]}
                            />
                        ))}
                    </Pie>
                    <Tooltip
                        contentStyle={{
                            background: "#020617",
                            border: "1px solid #27272a",
                            fontSize: 11,
                        }}
                        formatter={(v, name) => [`${v} trades`, name]}
                    />
                    <Legend
                        verticalAlign="bottom"
                        height={24}
                        wrapperStyle={{ fontSize: 10, color: "#a1a1aa" }}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}

function TickerDrilldownModal({ ticker, stats, trades, onClose }) {
    if (!ticker) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4">
            <div className="bg-zinc-900 rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden border border-zinc-700 shadow-xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
                    <div>
                        <div className="text-xs text-zinc-400">Ticker</div>
                        <div className="text-lg font-semibold text-amber-300">
                            {ticker}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="px-3 py-1 text-xs rounded-full bg-zinc-800 hover:bg-zinc-700"
                    >
                        Close
                    </button>
                </div>

                <div className="px-4 py-3 space-y-3">
                    {stats && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div>
                                <div className="text-zinc-400">Trades</div>
                                <div className="text-zinc-50 font-semibold">
                                    {stats.trades}
                                </div>
                            </div>
                            <div>
                                <div className="text-zinc-400">Wins</div>
                                <div className="text-emerald-300 font-semibold">
                                    {stats.wins}
                                </div>
                            </div>
                            <div>
                                <div className="text-zinc-400">Losses</div>
                                <div className="text-red-300 font-semibold">
                                    {stats.losses}
                                </div>
                            </div>
                            <div>
                                <div className="text-zinc-400">Total P/L</div>
                                <div
                                    className={
                                        stats.pnl >= 0
                                            ? "text-emerald-400 font-semibold"
                                            : "text-red-400 font-semibold"
                                    }
                                >
                                    {formatCurrency(stats.pnl)}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="text-xs text-zinc-400 mt-2 mb-1">
                        Trades for {ticker}
                    </div>

                    <div className="border border-zinc-800 rounded-xl max-h-56 overflow-y-auto text-xs">
                        {(!trades || trades.length === 0) && (
                            <div className="p-3 text-zinc-500">
                                No trades logged for this ticker yet.
                            </div>
                        )}
                        {trades &&
                            trades.map((t, idx) => {
                                const ts =
                                    t.closed_at ||
                                    t.filled_at ||
                                    t.created_at ||
                                    t.timestamp;
                                const date = ts ? String(ts).slice(0, 19) : "—";
                                const side = (t.side || "").toUpperCase();
                                const asset =
                                    (t.asset_type || "").toUpperCase() || "EQ";
                                const qty = t.qty || t.quantity || "-";
                                const price =
                                    t.avg_fill_price ||
                                    t.fill_price ||
                                    t.price ||
                                    "-";
                                const pnl =
                                    Number(
                                        t.realized_pl ??
                                        t.realized_pnl ??
                                        t.pnl ??
                                        t.pl ??
                                        0
                                    ) || 0;

                                return (
                                    <div
                                        key={idx}
                                        className="flex justify-between items-center px-3 py-2 border-b border-zinc-800 last:border-0"
                                    >
                                        <div className="mr-2 min-w-0">
                                            <div className="flex gap-1 items-center">
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-200">
                                                    {side || "TRADE"}
                                                </span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-900 text-zinc-400">
                                                    {asset}
                                                </span>
                                            </div>
                                            <div className="text-zinc-300 mt-0.5">
                                                {qty} @ {price}
                                            </div>
                                        </div>
                                        <div className="text-right text-[11px]">
                                            <div
                                                className={
                                                    pnl >= 0
                                                        ? "text-emerald-400"
                                                        : "text-red-400"
                                                }
                                            >
                                                {formatCurrency(pnl)}
                                            </div>
                                            <div className="text-zinc-500">
                                                {date}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function TradeAnalyticsCard({ userId }) {
    const [stats, setStats] = useState(null);
    const [pnlSeries, setPnlSeries] = useState([]);
    const [tickerPnl, setTickerPnl] = useState([]);
    const [tickerMeta, setTickerMeta] = useState({});
    const [rawTrades, setRawTrades] = useState([]);
    const [viewMode, setViewMode] = useState("daily");
    const [selectedTicker, setSelectedTicker] = useState(null);

    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        if (!userId) return;
        setLoading(true);
        setErr("");
        try {
            const { data } = await api.get("/api/trades/history", {
                params: { user_id: userId, page: 1, limit: 500 },
            });

            const trades = data.trades || data.items || data || [];
            if (!Array.isArray(trades) || trades.length === 0) {
                setStats(null);
                setPnlSeries([]);
                setTickerPnl([]);
                setTickerMeta({});
                setRawTrades([]);
                return;
            }

            setRawTrades(trades);

            let total = trades.length;
            let wins = 0;
            let losses = 0;
            let realized = 0;
            let equityCount = 0;
            let optionCount = 0;

            const daily = {};
            const perTicker = {};

            trades.forEach((t) => {
                const status = (t.status || "").toLowerCase();
                if (status === "success" || status === "win") wins += 1;
                else if (status === "loss" || status === "error") losses += 1;

                const pnl =
                    Number(
                        t.realized_pl ??
                        t.realized_pnl ??
                        t.pnl ??
                        t.pl ??
                        0
                    ) || 0;
                realized += pnl;

                const asset = (t.asset_type || "").toLowerCase();
                const isOption = asset === "option";

                if (isOption) optionCount += 1;
                else equityCount += 1;

                const ts =
                    t.closed_at || t.filled_at || t.created_at || t.timestamp;
                const d = ts ? String(ts).slice(0, 10) : "unknown";

                if (!daily[d]) {
                    daily[d] = { total: 0, equity: 0, option: 0 };
                }
                daily[d].total += pnl;
                if (isOption) daily[d].option += pnl;
                else daily[d].equity += pnl;

                const ticker = getTradeTicker(t);

                if (!perTicker[ticker]) {
                    perTicker[ticker] = {
                        pnl: 0,
                        trades: 0,
                        wins: 0,
                        losses: 0,
                    };
                }
                perTicker[ticker].pnl += pnl;
                perTicker[ticker].trades += 1;
                if (status === "success" || status === "win")
                    perTicker[ticker].wins += 1;
                else if (status === "loss" || status === "error")
                    perTicker[ticker].losses += 1;
            });

            const winRate = total ? (wins / total) * 100 : 0;
            const avgPnL = total ? realized / total : 0;

            const sortedDates = Object.keys(daily).sort((a, b) =>
                a.localeCompare(b)
            );
            let runningTotal = 0;
            let runningEquity = 0;
            let runningOption = 0;

            const pnlSeriesArr = sortedDates.map((date) => {
                const day = daily[date];
                runningTotal += day.total;
                runningEquity += day.equity;
                runningOption += day.option;

                return {
                    date,
                    pnlTotal: day.total,
                    pnlEquity: day.equity,
                    pnlOption: day.option,
                    cumTotal: runningTotal,
                    cumEquity: runningEquity,
                    cumOption: runningOption,
                };
            });

            const tickerArr = Object.entries(perTicker).map(
                ([ticker, v]) => ({
                    ticker,
                    pnl: v.pnl,
                    trades: v.trades,
                    wins: v.wins,
                    losses: v.losses,
                })
            );

            setStats({
                total,
                wins,
                losses,
                winRate,
                realized,
                avgPnL,
                equityCount,
                optionCount,
            });
            setPnlSeries(pnlSeriesArr);
            setTickerPnl(tickerArr);
            setTickerMeta(perTicker);
        } catch (e) {
            setErr("Could not load trade analytics.");
            setStats(null);
            setPnlSeries([]);
            setTickerPnl([]);
            setTickerMeta({});
            setRawTrades([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId]);

    const plClass =
        stats && stats.realized > 0
            ? "text-emerald-400"
            : stats && stats.realized < 0
                ? "text-red-400"
                : "text-zinc-100";

    const avgPlClass =
        stats && stats.avgPnL > 0
            ? "text-emerald-400"
            : stats && stats.avgPnL < 0
                ? "text-red-400"
                : "text-zinc-100";

    const selectedStats =
        selectedTicker && tickerMeta[selectedTicker]
            ? tickerMeta[selectedTicker]
            : null;

    const selectedTrades = selectedTicker
        ? rawTrades.filter((t) => getTradeTicker(t) === selectedTicker)
        : [];

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-4 mt-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Trade Analytics</h3>
                <button
                    onClick={load}
                    className="px-3 py-1 text-xs rounded bg-zinc-700 hover:bg-zinc-600"
                >
                    {loading ? "Refreshing…" : "Refresh"}
                </button>
            </div>

            {err && <div className="text-xs text-red-400">{err}</div>}

            {!err && !stats && !loading && (
                <div className="text-sm text-zinc-400">
                    No trade analytics yet — place some trades and they&apos;ll
                    show up here.
                </div>
            )}

            {stats && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div>
                            <div className="text-zinc-400">Total trades</div>
                            <div className="text-zinc-50 font-semibold">
                                {stats.total}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400">Win rate</div>
                            <div className="text-emerald-300 font-semibold">
                                {stats.winRate.toFixed(1)}%
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400">Realized P/L</div>
                            <div className={`${plClass} font-semibold`}>
                                {formatCurrency(stats.realized)}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400">Avg / trade</div>
                            <div className={`${avgPlClass} font-semibold`}>
                                {formatCurrency(stats.avgPnL)}
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 mt-3 text-xs items-center">
                        <div className="space-y-1">
                            <div className="text-zinc-400">Equity trades</div>
                            <div className="text-zinc-100 font-semibold">
                                {stats.equityCount}
                            </div>
                            <div className="text-zinc-400 mt-2">
                                Options trades
                            </div>
                            <div className="text-purple-300 font-semibold">
                                {stats.optionCount}
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <div className="text-xs text-zinc-400 mb-1">
                                Equity vs Options (by trade count)
                            </div>
                            <EquityOptionsPie
                                equityCount={stats.equityCount}
                                optionCount={stats.optionCount}
                            />
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <div className="text-xs text-zinc-400">
                                    P/L over time (
                                    {viewMode === "daily"
                                        ? "daily"
                                        : "cumulative"}
                                    )
                                </div>
                                <div className="inline-flex rounded-full bg-zinc-800 p-0.5 text-[10px]">
                                    <button
                                        onClick={() => setViewMode("daily")}
                                        className={`px-2 py-0.5 rounded-full ${viewMode === "daily"
                                                ? "bg-zinc-100 text-zinc-900"
                                                : "text-zinc-400"
                                            }`}
                                    >
                                        Daily
                                    </button>
                                    <button
                                        onClick={() => setViewMode("cumulative")}
                                        className={`px-2 py-0.5 rounded-full ${viewMode === "cumulative"
                                                ? "bg-zinc-100 text-zinc-900"
                                                : "text-zinc-400"
                                            }`}
                                    >
                                        Cum.
                                    </button>
                                </div>
                            </div>
                            <PnlOverTimeChart data={pnlSeries} viewMode={viewMode} />
                        </div>
                        <div>
                            <div className="text-xs text-zinc-400 mb-1">
                                P/L by ticker (click to drill down)
                            </div>
                            <TickerHeatmap
                                data={tickerPnl}
                                onSelectTicker={setSelectedTicker}
                            />
                        </div>
                    </div>

                    <TickerDrilldownModal
                        ticker={selectedTicker}
                        stats={selectedStats}
                        trades={selectedTrades}
                        onClose={() => setSelectedTicker(null)}
                    />
                </>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Unified positions                                                 */
/* ------------------------------------------------------------------ */

function UnifiedPositionsBlock({ userId }) {
    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-4">
            <h3 className="text-lg font-semibold">Positions &amp; Orders</h3>
            <PositionsAndOrdersCard userId={userId} />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Smart Exit Debug UI                                               */
/* ------------------------------------------------------------------ */

function SmartExitDebugCard({ userId, tradeMode, debugMode }) {
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);

    const run = async (execute) => {
        if (!userId) {
            alert("Please log in first.");
            return;
        }
        setLoading(true);
        setErr("");
        setResult(null);
        try {
            const { data } = await api.post(
                "/api/tasks/auto-manage-exits",
                {
                    user_id: userId,
                    mode: tradeMode,
                    debug: debugMode || true,
                    execute: !!execute,
                },
                { timeout: 60000 }
            );
            setResult(data || {});
        } catch (e) {
            console.error("SmartExitDebug error", e);
            const apiErr = e?.response?.data;
            setErr(
                apiErr?.error ||
                apiErr?.message ||
                e?.message ||
                "Exit engine call failed."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <h3 className="text-lg font-semibold">Smart Exit – Debug</h3>
            <p className="text-xs text-zinc-400">
                Simulate or run your Smart Exit engine for the current mode (
                <span className="font-semibold">{tradeMode}</span>). Debug mode
                returns detailed reasoning.
            </p>
            <div className="flex flex-wrap gap-2">
                <button
                    onClick={() => run(false)}
                    className="px-3 py-1.5 text-xs rounded-full bg-zinc-700 hover:bg-zinc-600"
                    disabled={loading}
                >
                    {loading ? "Running…" : "Simulate exits (no trades)"}
                </button>
                <button
                    onClick={() => run(true)}
                    className="px-3 py-1.5 text-xs rounded-full bg-rose-600 hover:bg-rose-500"
                    disabled={loading}
                >
                    {loading ? "Running…" : "Run exits now"}
                </button>
            </div>
            {err && <div className="text-xs text-red-400">{err}</div>}
            {result && (
                <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap max-h-56 overflow-y-auto border border-zinc-800 rounded p-2 bg-black/40">
                    {JSON.stringify(result, null, 2)}
                </pre>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Tabs + Floating buttons                                           */
/* ------------------------------------------------------------------ */

function TabButton({ id, label, icon, active, onClick }) {
    return (
        <button
            onClick={() => onClick(id)}
            className={`flex items-center gap-1 px-3 py-2 text-xs sm:text-sm border-b-2 -mb-px whitespace-nowrap ${active
                    ? "border-blue-500 text-blue-300"
                    : "border-transparent text-zinc-400 hover:text-zinc-200 hover:border-zinc-600"
                }`}
        >
            {icon && <span className="text-xs">{icon}</span>}
            <span>{label}</span>
        </button>
    );
}

function FloatingTradeButtons({ onGoToOrders, onGoToPositions }) {
    return (
        <div className="fixed bottom-4 right-4 z-40 flex flex-col gap-2 items-end">
            <button
                onClick={onGoToOrders}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-emerald-400 to-emerald-600 text-black shadow-[0_0_22px_rgba(16,185,129,0.9)] animate-pulse"
            >
                Trade
            </button>
            <button
                onClick={onGoToPositions}
                className="px-3 py-1 rounded-full text-xs bg-zinc-900/90 text-zinc-100 border border-zinc-600 hover:bg-zinc-800"
            >
                Positions
            </button>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  MAIN PAGE                                                         */
/* ------------------------------------------------------------------ */

export default function PaperTrading() {
    const [userId, setUserId] = useState("");
    const [activeTab, setActiveTab] = useState("overview");
    const [tradeMode, setTradeMode] = useState(() => {
        const saved = (localStorage.getItem("trade_mode") || "paper").toLowerCase();
        return saved === "live" ? "live" : "paper";
    });
    const [debugMode, setDebugMode] = useState(false);

    const positionsRef = useRef(null);

    useEffect(() => {
        setUserId(getUserId());
    }, []);

    useEffect(() => {
        localStorage.setItem("trade_mode", tradeMode);
    }, [tradeMode]);

    const goToOrders = () => {
        setActiveTab("orders");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const goToPositions = () => {
        if (positionsRef.current) {
            const rect = positionsRef.current.getBoundingClientRect();
            const top = rect.top + window.scrollY - 80;
            window.scrollTo({ top, behavior: "smooth" });
        } else {
            setActiveTab("overview");
        }
    };

    return (
        <div className="relative p-4 sm:p-6 text-white space-y-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-blue-400 mb-1">
                Paper &amp; Live Trading
            </h1>
            <p className="text-sm text-zinc-400 mb-2">
                Connect Alpaca &amp; Tradier, let the AI find plays, then trade
                everything in one place.
            </p>

            <TradeModeBadge />            
            <div className="flex items-center gap-2 text-xs mt-2">
                <span className="text-zinc-400">Mode:</span>
                <button
                    onClick={() => setTradeMode("paper")}
                    className={
                        "px-3 py-1 rounded-full transition " +
                        (tradeMode === "paper"
                            ? "bg-emerald-500 text-black"
                            : "text-zinc-300 hover:bg-zinc-800")
                    }
                >
                    Paper
                </button>
                <button
                    onClick={() => setTradeMode("live")}
                    className={
                        "px-3 py-1 rounded-full transition " +
                        (tradeMode === "live"
                            ? "bg-pink-500 text-black"
                            : "text-zinc-300 hover:bg-zinc-800")
                    }
                >
                    Live
                </button>
            </div>
            <AutoExitToggle userId={userId} />
            {/* Tabs */}
            <div className="flex gap-2 border-b border-zinc-800 mb-4 overflow-x-auto">
                <TabButton
                    id="overview"
                    label="Overview"
                    icon="📊"
                    active={activeTab === "overview"}
                    onClick={setActiveTab}
                />
                <TabButton
                    id="orders"
                    label="Orders"
                    icon="💸"
                    active={activeTab === "orders"}
                    onClick={setActiveTab}
                />
                <TabButton
                    id="ai"
                    label="AI"
                    icon="🤖"
                    active={activeTab === "ai"}
                    onClick={setActiveTab}
                />
                <TabButton
                    id="settings"
                    label="Settings"
                    icon="⚙️"
                    active={activeTab === "settings"}
                    onClick={setActiveTab}
                />
            </div>

            {/* OVERVIEW TAB */}
            {activeTab === "overview" && (
                <div className="grid gap-4 lg:grid-cols-3">
                    <div className="space-y-4 lg:col-span-2">
                        <BrokerHeader />
                        <div className="grid gap-4 md:grid-cols-2">
                            <BrokerConnectAlpaca userId={userId} />
                            <BrokerConnectTradier userId={userId} />
                        </div>
                        <BalancesCard userId={userId} />
                        <TradeAnalyticsCard userId={userId} />
                        <AllTradesCard userId={userId} />
                    </div>
                    <div ref={positionsRef} className="space-y-4">
                        <UnifiedPositionsBlock userId={userId} />
                    </div>
                </div>
            )}

            {/* ORDERS TAB */}
            {activeTab === "orders" && (
                <div className="grid gap-4 lg:grid-cols-3">
                    <div className="space-y-4 lg:col-span-2">
                        <PlaceOrderCard userId={userId} />
                        <PlaceOptionOrderCard userId={userId} />
                    </div>
                    <div ref={positionsRef} className="space-y-4">
                        <UnifiedPositionsBlock userId={userId} />
                    </div>
                </div>
            )}

            {/* AI TAB */}
            {activeTab === "ai" && (
                <div className="space-y-4">
                    <div className="mb-2 flex items-center space-x-3 text-xs">
                        <label className="font-semibold text-white/80">
                            Debug Mode
                        </label>
                        <input
                            type="checkbox"
                            checked={debugMode}
                            onChange={() => setDebugMode((v) => !v)}
                            className="w-4 h-4"
                        />
                        <span className="text-xs text-white/50">
                            Show detailed exit reasoning when running Smart Exit
                        </span>
                    </div>
                    <div className="grid gap-4 lg:grid-cols-3">
                        <div className="space-y-4 lg:col-span-2">
                            <PicksButtons userId={userId} tradeMode={tradeMode} />
                            <AutoTradeSummary userId={userId} />
                            <SmartExitDebugCard
                                userId={userId}
                                tradeMode={tradeMode}
                                debugMode={debugMode}
                            />
                        </div>
                        <div className="space-y-4">
                            <TradeSettingsCard userId={userId} />
                        </div>
                    </div>
                </div>
            )}

            {/* SETTINGS TAB */}
            {activeTab === "settings" && (
                <div className="grid gap-4 lg:grid-cols-3">
                    <div className="space-y-4 lg:col-span-2">
                        <TradeSettingsCard userId={userId} />
                        <AllTradesCard userId={userId} />
                    </div>
                    <div ref={positionsRef} className="space-y-4">
                        <UnifiedPositionsBlock userId={userId} />
                    </div>
                </div>
            )}

            <FloatingTradeButtons
                onGoToOrders={goToOrders}
                onGoToPositions={goToPositions}
            />
        </div>
    );
}
